import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ToasterState } from 'src/AppState/ToasterState';
import {
    getSelfExclusionConfig,
    SelfExclusionConfigType,
    updateSelfExclusionEmail,
} from 'src/api_openapi/v2/self-exclusion/selfExclusionConfigApi';

export class SelfExclusionConfigState {
    @observable public coolingOffHours: number | null = null;
    @observable public lockedAccountProperties: string[] = [];
    @observable public manualExclusionEmailEnabled = false;
    @observable public isLoading = true;
    @observable public isSaving = false;
    @observable public isReady = false;
    @observable public errorMessage: string | null = null;
    @observable private savedManualExclusionEmailEnabled = false;

    public constructor(private readonly appState: AppState) {
        makeObservable(this);
        void this.loadConfiguration();
    }

    @computed public get isDirty(): boolean {
        return this.isReady && this.manualExclusionEmailEnabled !== this.savedManualExclusionEmailEnabled;
    }

    @action public setManualExclusionEmailEnabled = (value: boolean): void => {
        this.manualExclusionEmailEnabled = value;
    };

    @action private applyConfiguration = (config: SelfExclusionConfigType): void => {
        this.coolingOffHours = config.coolingOffHours;
        this.lockedAccountProperties = config.lockedAccountProperties;
        this.manualExclusionEmailEnabled = config.manualExclusionEmailEnabled;
        this.savedManualExclusionEmailEnabled = config.manualExclusionEmailEnabled;
        this.isReady = true;
        this.errorMessage = null;
    };

    private get token(): string {
        return this.appState.common.session.currentJwt;
    }

    private get apiUrl(): string {
        return this.appState.common.appEnvParams.gatewayUrl;
    }

    @action public loadConfiguration = async (): Promise<void> => {
        this.isLoading = true;
        this.errorMessage = null;

        const result = await getSelfExclusionConfig(this.apiUrl, this.token);

        runInAction(() => {
            this.isLoading = false;

            if (result.type === 'error') {
                this.isReady = false;
                this.errorMessage = result.message;
                return;
            }

            this.applyConfiguration(result.config);
        });
    };

    @action public saveConfiguration = async (): Promise<void> => {
        if (!this.isDirty) {
            return;
        }

        this.isSaving = true;
        this.errorMessage = null;

        const result = await updateSelfExclusionEmail(this.apiUrl, this.token, this.manualExclusionEmailEnabled);

        if (result.type === 'error') {
            runInAction(() => {
                this.isSaving = false;
                this.errorMessage = result.message;
            });
            return;
        }

        runInAction(() => {
            this.isSaving = false;
            this.applyConfiguration(result.config);
        });

        ToasterState.get(this.appState.common).setToast({
            variant: 'success',
            title: 'Success',
            message: 'Self exclusion configuration saved.',
        });
    };
}
